import {
  SzApp,
  useProjectValidatePolicyActionsQuery,
  useUseProjectPolicyActionsHookGetProjectByIdQuery,
} from 'generated/graphql';
import {
  ACTION_CONFIGS,
  computeProjectStagesData,
  createPolicyValueMap,
  TProjectContext,
  TProjectPolicyActions,
  validateAction,
} from 'hooks/useProjectPolicyActions/useProjectPolicyActions.helpers';
import { useMemo } from 'react';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

/**
 * Tests rendering components that use this hook must provide BOTH the
 * ProjectValidatePolicyActions and UseProjectPolicyActionsHookGetProjectById
 * mocks. Use `policyActionsMocks()` from `./useProjectPolicyActions.mocks`.
 */
export const useProjectActionsValidation = ({
  projectId,
}: {
  projectId: number;
}) => {
  const { handleMsgType } = useNotification();

  const { data: policyActionsData, loading: loadingPolicyActions } =
    useProjectValidatePolicyActionsQuery({
      variables: {
        projectId,
        actions: ACTION_CONFIGS.map((config) => config.action),
        app: SzApp.AdminApp,
      },
      skip: !projectId,
      onError: (error) => {
        handleMsgType({
          type: TNotification.error,
          title: 'Unable to get role policy actions',
          message: error?.message || 'Something went wrong',
          dismissable: true,
        });
      },
    });

  const { data: projectByIdData, loading: loadingProjectById } =
    useUseProjectPolicyActionsHookGetProjectByIdQuery({
      variables: {
        id: projectId,
      },
      skip: !projectId,
    });

  const projectStagesData = useMemo(
    () =>
      computeProjectStagesData(
        projectByIdData?.project?.interviewStages,
        projectByIdData?.project?.assessmentStagesV2,
        projectByIdData?.project?.shortlistStages,
      ),
    [
      projectByIdData?.project?.interviewStages,
      projectByIdData?.project?.assessmentStagesV2,
      projectByIdData?.project?.shortlistStages,
    ],
  );

  const validationContext: TProjectContext = useMemo(
    () => ({
      stagesData: projectStagesData,
      projectStatus: projectByIdData?.project?.status,
      projectSource: projectByIdData?.project?.source,
    }),
    [projectStagesData, projectByIdData?.project?.status],
  );

  // BE returns an array of boolean values for each action, we need to create a map of action to policy value
  const policyValueMap = useMemo(
    () => createPolicyValueMap(policyActionsData?.ProjectValidatePolicyActions),
    [policyActionsData?.ProjectValidatePolicyActions],
  );

  const projectPolicyActions: TProjectPolicyActions = useMemo(() => {
    // If data is still loading, all actions are disabled
    if (loadingPolicyActions || loadingProjectById) {
      return Object.fromEntries(
        ACTION_CONFIGS.map((config) => [config.action, false]),
      ) as TProjectPolicyActions;
    }

    // Validate each action based on its configuration
    return Object.fromEntries(
      ACTION_CONFIGS.map((config) => [
        config.action,
        validateAction(
          config,
          policyValueMap.get(config.action) ?? false,
          validationContext,
        ),
      ]),
    ) as TProjectPolicyActions;
  }, [
    loadingPolicyActions,
    loadingProjectById,
    policyValueMap,
    validationContext,
  ]);

  return {
    projectPolicyActions,
    projectStatus: projectByIdData?.project?.status,
  };
};
